// packages/shared/src/widget-field-states.ts
//
// #793 — the venue's Off / Optional / Required (#792, ruling R13) as the
// PUBLIC booking widget needs to see it.
//
// #792 stores the switch as flags on the field objects inside the tenant's
// `newBookingFormConfig` blob: `hidden: true` = Off, `required: true` =
// Required, neither = Optional. The staff-side reader is
// `src/shared/newBookingFormConfig.js` — a frontend ESM module the middleware
// cannot import — so the MINIMAL rule is re-derived here, in the one package
// both sides already share. Pure, no I/O, runs identically in Node and the
// browser (same contract as deposit-rules.ts).
//
// Deliberately NARROWER than #792's six-key hideable roster
// (dietary, specialHandling, occasion, seatingPreference, bookingOrigin,
// notes): the widget renders an input for only THREE of them. Publishing the
// other three on an unauthenticated endpoint would be venue-config leakage
// with no guest-visible purpose — DR-PRIVACY-01 minimisation. Adding a fourth
// key here is a deliberate act: it widens a public payload.
//
// NOT Zod-schema'd on purpose: `@yebo/shared` carries ZERO runtime
// dependencies today (its "validators" are hand-rolled predicates) and the
// embeddable widget imports runtime values from this package's barrel —
// pulling zod in for one three-key object would be a package-boundary change
// for no safety gain, since the input here is arbitrary tenant-stored JSON
// that this function must degrade on rather than reject. The union type below
// is the contract; every unparseable shape resolves to "optional".

/** Off = do not ask; Optional = ask, may be blank; Required = must be filled. */
export type WidgetFieldState = 'off' | 'optional' | 'required';

/**
 * The ONLY form fields whose state reaches the public widget config — the
 * three the widget actually renders an input for.
 */
export const WIDGET_FIELD_KEYS = ['dietary', 'seatingPreference', 'occasion'] as const;

export type WidgetFieldKey = (typeof WIDGET_FIELD_KEYS)[number];

/** `fields` on GET /api/widget/config/:locationId. Always all three keys. */
export type WidgetFieldStates = Record<WidgetFieldKey, WidgetFieldState>;

/** Mirrors `compactKey` in src/shared/newBookingFormConfig.js so the two sides
 *  agree on what "the dietary field" is called (`Seating_Preference`,
 *  `seating preference` and `seatingPreference` are the same field). */
const compactKey = (key: unknown): string =>
  String(key ?? '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');

const KEY_BY_COMPACT = new Map<string, WidgetFieldKey>(
  WIDGET_FIELD_KEYS.map((k) => [compactKey(k), k]),
);

const allOptional = (): WidgetFieldStates => ({
  dietary: 'optional',
  seatingPreference: 'optional',
  occasion: 'optional',
});

/** The blob may be an object, or the JSON string the config custom value
 *  stores. Anything else — or unparseable JSON — is "no config". */
function fieldsOf(newBookingFormConfig: unknown): unknown[] | null {
  let blob: unknown = newBookingFormConfig;
  if (typeof blob === 'string') {
    const trimmed = blob.trim();
    if (!trimmed) return null;
    try {
      blob = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  if (!blob || typeof blob !== 'object') return null;
  const fields = (blob as { fields?: unknown }).fields;
  return Array.isArray(fields) && fields.length > 0 ? fields : null;
}

/**
 * Resolve the three widget-rendered fields' states from a tenant's
 * `newBookingFormConfig`.
 *
 * `hidden` → "off" (Off wins over a stale `required: true` — a field nobody is
 * asked for can never block a booking, matching the staff normalizer);
 * `required` → "required"; otherwise "optional". A field missing from
 * `fields[]`, a missing/invalid/unparseable config, or an empty roster all
 * resolve to "optional" — i.e. exactly what every venue does today. First
 * matching entry per key wins.
 */
export function widgetFieldStates(newBookingFormConfig: unknown): WidgetFieldStates {
  const states = allOptional();
  const fields = fieldsOf(newBookingFormConfig);
  if (!fields) return states;

  const seen = new Set<WidgetFieldKey>();
  for (const field of fields) {
    if (!field || typeof field !== 'object') continue;
    const entry = field as { key?: unknown; hidden?: unknown; required?: unknown };
    const key = KEY_BY_COMPACT.get(compactKey(entry.key));
    if (!key || seen.has(key)) continue;
    seen.add(key);
    states[key] = entry.hidden ? 'off' : entry.required ? 'required' : 'optional';
  }
  return states;
}
